import { auth } from "../../firebase";
import { jwtDecode } from "jwt-decode";
import { decryptData } from "../../utils/cloudApi";

let currentIdToken = null;

const isTokenValid = (token) => {
  try {
    // Decode the token without verifying the signature.
    const decoded = jwtDecode(token);
    // Check if the token has an expiration (exp is in seconds).
    if (!decoded.exp) return false;

    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime;
  } catch (error) {
    console.error("Failed to decode token:", error);
    return false;
  }
};

async function getFreshIdToken() {
  if (!auth.currentUser) {
    return null;
  }

  // if we’ve never fetched one yet, or it’s expired, force a refresh
  if (!currentIdToken || !isTokenValid(currentIdToken)) {
    currentIdToken = await auth.currentUser.getIdToken(/* forceRefresh */ true);
  }

  return currentIdToken;
}
/**
 * Generate PDF by calling Cloudflare Worker API
 * @param {Object} data - Invoice data
 * @param {string} action - 'download', 'preview', or 'print'
 * @param {string} cid - Company ID
 * @param {string} formType - Form type ('inv' for invoice)
 * @param {Array} templates - Array of available templates
 * @param {string} userId - User ID
 * @returns {Promise<Blob|Object>} PDF blob or error object
 */
export const generatePDF = async (
  data,
  action,
  cid,
  formType,
  templates,
  userId,
  privateKey
) => {
  try {
    let template =
      data.templateRef && templates
        ? templates.find((tpl) => tpl.id === data.templateRef)
        : null;

    // Define default values
    const defaultTemplate = {
      headerColor: [0, 0, 0],
      tableHeaderColor: [43, 112, 115],
      tableHeaderTextColor: [255, 255, 255],
    };

    const decrypted = await decryptData(template, userId, privateKey);

    // Overwrite template with defaults where needed
    template = {
      ...defaultTemplate,
      ...(decrypted || {}),
    };

    // Prepare request body
    const requestBody = {
      data,
      template,
      cid,
      formType,
    };

    const firebaseToken = await getFreshIdToken();

    // Call your Cloudflare Worker endpoint
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_BASE_API}/generateInvoice`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`,
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: await response.text() };
      }
      console.error("❌ API error:", errorData);
      throw new Error(
        errorData.message || errorData.error || "Failed to generate PDF"
      );
    }

    // Get the PDF as a blob
    const blob = await response.blob();

    const tempCid = cid || "CID000XX";
    const tempType = formType === "inv" ? "SQ-" : "";
    const filename = `${tempCid}_${tempType}${data.invoiceNo}.pdf`;

    // Handle different actions
    if (action === "download") {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return { success: true, filename };
    } else {
      // Return blob for other uses
      return blob;
    }
  } catch (error) {
    console.error("💥 PDF generation failed:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};
